//do not import from index here, we do not want to bundle the whole cf+core into the debug build
import { getDefaultGetArticleDataOptions } from "../cf/Defaults";
import * as basket from "../eaiws/basket";
import { Log } from "../utils";
import { forEachParallel, Lock } from "../utils/async";
import { isNullOrEmpty } from "../utils/string";
import { BasketItem } from "./BasketItem";
import { ItemUpdateFlags } from "./BaseItem";
import { MAX_CONCURRENT_TASKS } from "./Constants";
export class ArticleItem extends BasketItem {
    get isOfmlArticle() {
        return (true);
    }
    //currently known ofml update state
    get ofmlUpdateState() {
        return (this.mOfmlUpdateState);
    }
    constructor(pManager, pParent, pChildIndex, pEaiwsItem) {
        super(pManager, pParent, pChildIndex, pEaiwsItem);
        this.mCameraSetup = null;
        this.mPreferStoredImage = true;
        this.mTaxInfo = null;
        this.mTaxInfoLock = new Lock();
        this.mUserDescription = null;
        this.mUserDescriptionLock = new Lock();
        this.mCompositeCalculationHdrVersion = this.mManager._getHeaderCalcVersion();
        //Note: handleChangedItemProps() was called before
        if (this.mOfmlUpdateState == null)
            this.mOfmlUpdateState = "Unknown";
    }
    getMainArticle() {
        const tMainId = this.mItem.getMainArticleId();
        if (tMainId == null || tMainId === this.id)
            return (this);
        else {
            const tMain = this.mManager.getItemById(tMainId);
            return (tMain != null ? tMain : this);
        }
    }
    isSubArticle() {
        const tMainId = this.mItem.getMainArticleId();
        if (tMainId == null || tMainId === this.id)
            return (false);
        else
            return (true);
    }
    getSubArticles(pRecursive) {
        const tSubArticles = new Array();
        this.internalGetSubArticles(pRecursive, tSubArticles);
        return (tSubArticles);
    }
    /**
     * Updates the main article with all its sub articles (and there sub articles).
     * Note: BasketItemUpdateFlags.IncludeChildren will be ignored here.
     */
    async updateWholeComposite(pFlags) {
        try {
            this.mManager._beginCaching();
            const tMainArticle = this.getMainArticle();
            const tFlags = pFlags & ~ItemUpdateFlags.IncludeChildren; //remove IncludeChildren flag
            if ((pFlags & ItemUpdateFlags.Item) != 0)
                await this.mManager._fetchEaiwsItems([tMainArticle.id], false, true);
            await tMainArticle.updateItem(tFlags);
            await tMainArticle.updateSubArticles(tFlags);
        }
        finally {
            this.mManager._endCaching();
        }
    }
    async setQuantity(pQuantity) {
        const tMainArticle = this.getMainArticle();
        if (tMainArticle != this)
            return (tMainArticle.setQuantity(pQuantity));
        const tNewProps = new basket.ItemProperties();
        tNewProps.article = new basket.ArticleProperties();
        tNewProps.article.quantity = pQuantity;
        await this.mManager.session.basket.setItemProperties(this.id, tNewProps);
        await this.updateWholeComposite(ItemUpdateFlags.ItemProps | ItemUpdateFlags.Calculation);
    }
    /**
     * Returns a composed calculation for the main article including all its sub articles.
     * @param pIgnoreHeaderCalculation If true the cached calculation will be returned even if the
     *      header calculation has changed in the meantime. Every condition which depends on a header condition
     *      may have a wrong/outdated value in this case. (default: false)
     */
    async getCompositeCalculation(pIgnoreHeaderCalculation = false) {
        const tMainArticle = this.getMainArticle();
        if (tMainArticle != this)
            return (tMainArticle.getCompositeCalculation());
        const tHdrValid = pIgnoreHeaderCalculation ? true : this.mCompositeCalculationHdrVersion === this.mManager._getHeaderCalcVersion();
        if (this.mCompositeCalculation !== undefined && tHdrValid)
            return (this.mCompositeCalculation);
        else {
            this.mCompositeCalculationHdrVersion = this.mManager._getHeaderCalcVersion();
            const tInfo = await this.mManager._getCompositeCalculation(this);
            this.mCompositeCalculation = tInfo;
            return (tInfo);
        }
    }
    /**
     * Retrieves the ofml update state if it is currently unkown.
     */
    fetchOfmlUpdateState() {
        //forward to main article
        const tMain = this.getMainArticle();
        if (tMain !== this)
            return (tMain.fetchOfmlUpdateState());
        return (this.mManager.fetchOfmlUpdateStates([this]));
    }
    setShortText(pText) {
        //do not update the item label here because for ofml articles the product shorttext is used as label
        return (ArticleItem.SetShortText(this, pText, false));
    }
    async setLongText(pText) {
        const tText = await BasketItem.BuildDisplayText(this.basket, pText);
        const tArtDesc = new basket.ArticleDescription();
        tArtDesc.longText = [tText];
        const tProps = new basket.ItemProperties();
        tProps.article = new basket.ArticleProperties();
        tProps.article.userDescription = tArtDesc;
        return (this.setItemProperties(tProps, false, false, ItemUpdateFlags.UserItemDescription));
    }
    async setFeatureText(pText) {
        const tText = await BasketItem.BuildDisplayText(this.basket, pText);
        const tArtDesc = new basket.ArticleDescription();
        tArtDesc.featureText = [tText];
        const tProps = new basket.ItemProperties();
        tProps.article = new basket.ArticleProperties();
        tProps.article.userDescription = tArtDesc;
        return (this.setItemProperties(tProps, false, false, ItemUpdateFlags.UserItemDescription));
    }
    setPackagingNetWeight(pWeight, pUnit) {
        const tProps = new basket.ItemProperties();
        tProps.article = new basket.ArticleProperties();
        tProps.article.packagingInfo = new basket.PackagingInfo();
        tProps.article.packagingInfo.netWeight = new basket.Quantity();
        tProps.article.packagingInfo.netWeight.value = pWeight;
        tProps.article.packagingInfo.netWeight.unit = pUnit;
        return (this.setItemProperties(tProps, false, true));
    }
    setPackagingVolume(pVolume, pUnit) {
        const tProps = new basket.ItemProperties();
        tProps.article = new basket.ArticleProperties();
        tProps.article.packagingInfo = new basket.PackagingInfo();
        tProps.article.packagingInfo.volume = new basket.Quantity();
        tProps.article.packagingInfo.volume.value = pVolume;
        tProps.article.packagingInfo.volume.unit = pUnit;
        return (this.setItemProperties(tProps, false, false));
    }
    async getTaxInformation() {
        const tLockContext = await this.mTaxInfoLock.acquireLock();
        try {
            if (this.mTaxInfo != null)
                return (this.mTaxInfo);
            this.mTaxInfo = await this.mManager.session.basket.getTaxInformation(this.id);
            return (this.mTaxInfo);
        }
        finally {
            tLockContext.unlock();
        }
    }
    async setTaxInformation(pTaxType, pTaxCategory) {
        await this.mManager.session.basket.setTaxInformation(this.id, pTaxType, pTaxCategory);
        return this.updateItem(ItemUpdateFlags.Tax | ItemUpdateFlags.Calculation | ItemUpdateFlags.ItemProps);
    }
    /**
     * Returns a user defined description for the article
     * Use `setShortText()`, `setLongText()` or `setFeatureText()` to change the description.
     */
    async getUserDescription() {
        var _a, _b;
        const tLockContext = await this.mUserDescriptionLock.acquireLock();
        try {
            if (this.mUserDescription != null)
                return (this.mUserDescription);
            const tOptions = new basket.GetItemPropertiesOptions();
            tOptions.textMode = "Simple";
            const tProps = await this.mManager.session.basket.getItemProperties([this.id], tOptions);
            const tDesc = (_b = (_a = tProps[0]) === null || _a === void 0 ? void 0 : _a.article) === null || _b === void 0 ? void 0 : _b.userDescription;
            this.mUserDescription = tDesc !== null && tDesc !== void 0 ? tDesc : new basket.ArticleDescription();
            return (this.mUserDescription);
        }
        finally {
            tLockContext.unlock();
        }
    }
    getCameraSetup() {
        return (this.mCameraSetup);
    }
    async setCameraSetup(pSetup, pUpdateItem = true) {
        this.mCameraSetup = pSetup;
        if (pUpdateItem) {
            return (this.updateItem(ItemUpdateFlags.Image));
        }
    }
    //// protected /////////////////////////////////////////////////////////////////////////////
    static async SetShortText(pItem, pText, pUpdateItemLabel) {
        const tText = await BasketItem.BuildDisplayText(pItem.basket, pText);
        const tArtDesc = new basket.ArticleDescription();
        tArtDesc.shortText = [tText];
        const tProps = new basket.ItemProperties();
        tProps.article = new basket.ArticleProperties();
        tProps.article.userDescription = tArtDesc;
        return (pItem.setItemProperties(tProps, pUpdateItemLabel, false, ItemUpdateFlags.UserItemDescription));
    }
    static SetManufacturerId(pItem, pId) {
        const tProps = new basket.ItemProperties();
        tProps.article = new basket.ArticleProperties();
        tProps.article.manufacturerId = pId.replace(/[^a-zA-Z0-9]/g, ''); //only alpha numeric values allowed
        return (pItem.setItemProperties(tProps, false, false));
    }
    static SetSeriesId(pItem, pId) {
        const tProps = new basket.ItemProperties();
        tProps.article = new basket.ArticleProperties();
        tProps.article.seriesId = pId.replace(/[^a-zA-Z0-9_]/g, '_'); //only alpha numeric values and underscore allowed
        return (pItem.setItemProperties(tProps, false, false));
    }
    static SetArticleNumber(pItem, pArtNbr) {
        const tProps = new basket.ItemProperties();
        tProps.article = new basket.ArticleProperties();
        tProps.article.baseArticleNumber = pArtNbr;
        tProps.article.finalArticleNumber = pArtNbr;
        return (pItem.setItemProperties(tProps, false, false));
    }
    performUpdates(pFlags) {
        //image was updated -> no longer prefer stored image
        if ((pFlags & ItemUpdateFlags.Image) != 0)
            this.mPreferStoredImage = false;
        if ((pFlags & ItemUpdateFlags.Tax) != 0)
            this.mTaxInfo = null;
        if ((pFlags & ItemUpdateFlags.UserItemDescription) != 0)
            this.mUserDescription = null;
        if ((pFlags & ItemUpdateFlags.ConfigData) != 0)
            this.mConfigData = undefined;
        return (super.performUpdates(pFlags));
    }
    invalidateCalculation(pTriggerUpdateEvent) {
        this.mCompositeCalculation = undefined;
        super.invalidateCalculation(pTriggerUpdateEvent);
    }
    async generateImageUrl() {
        this.mImageUrl = null;
        //try to get stored image first
        if (this.mCameraSetup == null || this.mPreferStoredImage) {
            const tStoredImage = await this.getStoredImage();
            if (tStoredImage != null) {
                await this.setCameraSetup(this.getCameraSetupFromImage(tStoredImage), false);
                this.mImageUrl = tStoredImage.url;
                return;
            }
        }
        //generate new image on server
        const tGeneratedImage = await this.getGeneratedImage(this.mCameraSetup);
        if (!isNullOrEmpty(tGeneratedImage)) {
            this.mImageUrl = tGeneratedImage;
            return;
        }
        //use stored image as fallback if not already requested
        if (!this.mPreferStoredImage) {
            const tStoredImage = await this.getStoredImage();
            if (tStoredImage != null) {
                this.mImageUrl = tStoredImage.url;
                return;
            }
        }
        //use catalog image as fallback
        const tCatalogImage = await this.getCatalogImage();
        if (!isNullOrEmpty(tCatalogImage)) {
            this.mImageUrl = tCatalogImage;
        }
    }
    handleChangedItemProps() {
        if (this.mItemProps != null && this.mItemProps.article != null && this.mItemProps.article.ofmlUpdateState != null) {
            //do not override known states, item props do not return states like "NoCatalog" or "Invalid"
            if (this.mOfmlUpdateState == null || this.mItemProps.article.ofmlUpdateState === "UpToDate") {
                this.mOfmlUpdateState = this.mItemProps.article.ofmlUpdateState;
            }
        }
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    _preferStoredImage() {
        return (this.mPreferStoredImage);
    }
    /** @internal */
    _setOfmlUpdateState(pState, pUpdateItemProps) {
        if (this.mOfmlUpdateState === pState)
            return;
        this.mOfmlUpdateState = pState;
        if (pUpdateItemProps && this.mItemProps != null && this.mItemProps.article != null) {
            this.mItemProps.article.ofmlUpdateState = pState;
        }
        //trigger event
        this.mManager.eventOfmlUpdateStateChanged.trigger(this);
        //set state for sub articles
        const tSubArticles = this.getSubArticles(false);
        for (const tSub of tSubArticles) {
            tSub._setOfmlUpdateState(pState, pUpdateItemProps);
        }
    }
    /** @internal */
    _getCachedConfigData() {
        return (this.mConfigData);
    }
    /** @internal */
    _setCachedConfigData(pData) {
        this.mConfigData = pData;
    }
    /** @internal */
    _getArticleData(pOptions) {
        const tRequestedOptions = pOptions !== null && pOptions !== void 0 ? pOptions : getDefaultGetArticleDataOptions();
        tRequestedOptions.separateCurrencies = true; //force to be true
        //check cache first
        const tArticleData = this._getCachedArticleData(tRequestedOptions);
        if (tArticleData != null)
            return (Promise.resolve(tArticleData));
        let tPromise = this.mManager.session.basket.getArticleData(this.id, tRequestedOptions);
        tPromise = tPromise.then((pArticleData) => {
            //fill/update cache
            if (this.mConfigData == null)
                this.mConfigData = {};
            const tCachedOptions = this.mConfigData.articleDataOptions;
            const tCachedData = this.mConfigData.articleData;
            if (tCachedOptions == null || tCachedData == null) {
                //nothing cached yet, add entry
                const tOptions = new basket.GetArticleDataOptions();
                Object.assign(tOptions, tRequestedOptions); //create copy
                this.mConfigData.articleData = pArticleData;
                this.mConfigData.articleDataOptions = tOptions;
            }
            else {
                //update cached articledata
                if (pArticleData != null) {
                    //properties
                    if (!tRequestedOptions.noProperties) {
                        tCachedOptions.noProperties = false;
                        tCachedData.properties = pArticleData.properties;
                        tCachedData.propertyClasses = pArticleData.propertyClasses;
                    }
                    //catalog icon
                    if (tRequestedOptions.fetchCatalogIcon) {
                        tCachedOptions.fetchCatalogIcon = true;
                        tCachedData.catalogIcon = pArticleData.catalogIcon;
                    }
                    //catalog image
                    if (tRequestedOptions.fetchCatalogImage) {
                        tCachedOptions.fetchCatalogImage = true;
                        tCachedData.catalogImage = pArticleData.catalogImage;
                    }
                }
            }
            return (pArticleData);
        });
        return (tPromise);
    }
    updateSubArticles(pFlags) {
        const tSubArticles = this.getSubArticles(false);
        const tPromise = forEachParallel(tSubArticles, async (pIndex, pArticle) => {
            await pArticle.updateItem(pFlags);
            await pArticle.updateSubArticles(pFlags);
        }, MAX_CONCURRENT_TASKS);
        return (tPromise.then(() => { }));
    }
    internalGetSubArticles(pRecursive, pResult) {
        if (this.mItem.subArticleIds != null) {
            for (const tSubId of this.mItem.subArticleIds) {
                const tSubArticle = this.mManager.getItemById(tSubId);
                if (tSubArticle != null) {
                    pResult.push(tSubArticle);
                    if (pRecursive)
                        tSubArticle.internalGetSubArticles(pRecursive, pResult);
                }
                else
                    Log.warn("Unknown sub article: " + tSubId);
            }
        }
    }
    _getCachedArticleData(pOptions) {
        let tRequestedOptions = pOptions;
        if (tRequestedOptions == null)
            tRequestedOptions = getDefaultGetArticleDataOptions();
        if (this.mConfigData == null || this.mConfigData.articleDataOptions == null)
            return (undefined); //nothing cached
        //compare cached with requested options
        const tCachedOptions = this.mConfigData.articleDataOptions;
        if (tCachedOptions == null)
            return (undefined); //nothing cached
        //properties not cached
        if (tCachedOptions.noProperties && !tRequestedOptions.noProperties)
            return (undefined);
        //icon not cached
        if (!tCachedOptions.fetchCatalogIcon && tRequestedOptions.fetchCatalogIcon)
            return (undefined);
        //image not cached
        if (!tCachedOptions.fetchCatalogImage && tRequestedOptions.fetchCatalogImage)
            return (undefined);
        return (this.mConfigData.articleData);
    }
}
